// src/pages/Integrations.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Nav, Tab, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationService } from '../api/services/integrations';
import { useToastStore } from '../components/UI/Toast';

/**
 * Komponenta stránky integrací
 * Umožňuje uživatelům propojit monitorovací službu s externími nástroji
 */
const Integrations = () => {
    const [activeTab, setActiveTab] = useState('slack');
    const queryClient = useQueryClient();
    const addToast = useToastStore(state => state.addToast);

    const { data: integrations, isLoading, error } = useQuery({
        queryKey: ['integrations'],
        queryFn: () => integrationService.getIntegrations().then(res => res.data),
        onError: () => {
            addToast('Nepodařilo se načíst integrace', 'danger');
        }
    });

    // Connect integration mutation
    const connectMutation = useMutation(

        {
            mutationFn: ({ type, data }) => {
                if (type === 'slack') {
                    return integrationService.connectSlack(data);
                } else if (type === 'discord') {
                    return integrationService.connectDiscord(data);
                } else if (type === 'telegram') {
                    return integrationService.connectTelegram(data);
                } else if (type === 'asana') {
                    return integrationService.connectAsana(data);
                } else if (type === 'trello') {
                    return integrationService.connectTrello(data);
                }
            },
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries(['integrations']);
                addToast(`${variables.type.charAt(0).toUpperCase() + variables.type.slice(1)} integrace byla úspěšně připojena`, 'success');
            },
            onError: (error, variables) => {
                addToast(`Připojení ${variables.type} selhalo: ${error.response?.data?.message || 'Neznámá chyba'}`, 'danger');
            }
        }
    );

    // Disconnect integration mutation
    const disconnectMutation = useMutation(

        {
            mutationFn: (type) => integrationService.disconnectIntegration(type),
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries(['integrations']);
                addToast(`${variables.charAt(0).toUpperCase() + variables.slice(1)} integrace byla odpojena`, 'success');
            },
            onError: (error, variables) => {
                addToast(`Odpojení ${variables} selhalo: ${error.response?.data?.message || 'Neznámá chyba'}`, 'danger');
            }
        }
    );

    // Test integration mutation
    const testIntegrationMutation = useMutation(

        {
            mutationFn: (type) => integrationService.testIntegration(type),
            onSuccess: (data, variables) => {
                addToast(`Testovací zpráva byla odeslána na ${variables}`, 'success');
            },
            onError: (error, variables) => {
                addToast(`Testování ${variables} selhalo: ${error.response?.data?.message || 'Neznámá chyba'}`, 'danger');
            }
        }
    );

    // Form states pro různé integrace
    const [slackForm, setSlackForm] = useState({
        webhookUrl: '',
        channel: '#monitoring'
    });

    const [discordForm, setDiscordForm] = useState({
        webhookUrl: ''
    });

    const [telegramForm, setTelegramForm] = useState({
        botToken: '',
        chatId: ''
    });

    const [asanaForm, setAsanaForm] = useState({
        apiKey: '',
        projectId: '',
        assigneeId: ''
    });

    const [trelloForm, setTrelloForm] = useState({
        apiKey: '',
        token: '',
        boardId: '',
        listId: ''
    });

    // Handlery pro formuláře
    const handleSlackSubmit = (e) => {
        e.preventDefault();
        connectMutation.mutate({ type: 'slack', data: slackForm });
    };

    const handleDiscordSubmit = (e) => {
        e.preventDefault();
        connectMutation.mutate({ type: 'discord', data: discordForm });
    };

    const handleTelegramSubmit = (e) => {
        e.preventDefault();
        connectMutation.mutate({ type: 'telegram', data: telegramForm });
    };

    const handleAsanaSubmit = (e) => {
        e.preventDefault();
        connectMutation.mutate({ type: 'asana', data: asanaForm });
    };

    const handleTrelloSubmit = (e) => {
        e.preventDefault();
        connectMutation.mutate({ type: 'trello', data: trelloForm });
    };

    const handleDisconnect = (type) => {
        const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);

        if (window.confirm(`Opravdu chcete odpojit ${capitalizedType} integraci?`)) {
            disconnectMutation.mutate(type);
        }
    };

    const handleTestIntegration = (type) => {
        testIntegrationMutation.mutate(type);
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání integrací...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="m-4">
                <Alert.Heading>Chyba při načítání integrací</Alert.Heading>
                <p>{error.response?.data?.message || 'Nepodařilo se načíst integrace. Zkuste to prosím znovu.'}</p>
            </Alert>
        );
    }

    return (
        <Container fluid className="">
            <h1 className="mb-4 ">Integrace</h1>

            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="pills" className="mb-4">
                    <Nav.Item>
                        <Nav.Link eventKey="slack">
                            <i className="fab fa-slack me-2"></i> Slack
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="discord">
                            <i className="fab fa-discord me-2"></i> Discord
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="telegram">
                            <i className="fab fa-telegram me-2"></i> Telegram
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="asana">
                            <i className="fas fa-tasks me-2"></i> Asana
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="trello">
                            <i className="fab fa-trello me-2"></i> Trello
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="webhooks">
                            <i className="fas fa-plug me-2"></i> Webhooks
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    {/* Slack integrace */}
                    <Tab.Pane eventKey="slack">
                        <Card className="shadow-sm">
                            <Card.Header className="bg-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <div className="me-3">
                                            <i className="fab fa-slack fa-2x text-primary"></i>
                                        </div>
                                        <div>
                                            <h4 className="mb-0">Slack integrace</h4>
                                            <p className="mb-0 text-muted">Propojte monitoring s vašimi Slack kanály</p>
                                        </div>
                                    </div>
                                    <div>
                                        {integrations?.slack ? (
                                            <div className="d-flex align-items-center">
                                                <Badge bg="success" className="me-3 px-3 py-2">Připojeno</Badge>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDisconnect('slack')}
                                                    disabled={disconnectMutation.isLoading}
                                                >
                                                    {disconnectMutation.isLoading ? (
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                    ) : 'Odpojit'}
                                                </Button>
                                            </div>
                                        ) : (
                                            <Badge bg="secondary" className="px-3 py-2">Nepřipojeno</Badge>
                                        )}
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {connectMutation.isError && (
                                    <Alert variant="danger" dismissible>
                                        Připojení selhalo. Zkontrolujte své údaje a zkuste to znovu.
                                    </Alert>
                                )}

                                {!integrations?.slack ? (
                                    <Form onSubmit={handleSlackSubmit}>
                                        <Row>
                                            <Col lg={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Webhook URL</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={slackForm.webhookUrl}
                                                        onChange={(e) => setSlackForm({ ...slackForm, webhookUrl: e.target.value })}
                                                        placeholder="https://hooks.slack.com/services/..."
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col lg={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>ID přiřazené osoby (volitelné)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={asanaForm.assigneeId}
                                                        onChange={(e) => setAsanaForm({ ...asanaForm, assigneeId: e.target.value })}
                                                        placeholder="1234567890123456"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={connectMutation.isLoading}
                                        >
                                            {connectMutation.isLoading ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Připojování...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-tasks me-2"></i> Připojit Asana
                                                </>
                                            )}
                                        </Button>
                                    </Form>
                                ) : (
                                    <div className="bg-light p-4 rounded">
                                        <div className="mb-4">
                                            <h5>Aktuální nastavení</h5>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <p className="mb-1"><strong>Projekt:</strong> {integrations.asana.projectName}</p>
                                                    {integrations.asana.assigneeName && (
                                                        <p className="mb-0"><strong>Přiřazeno:</strong> {integrations.asana.assigneeName}</p>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <p className="mb-0"><strong>Poslední synchronizace:</strong> {integrations.asana.lastSync ? new Date(integrations.asana.lastSync).toLocaleString() : 'Žádná synchronizace'}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <h5>Nastavení vytváření úkolů</h5>
                                            <Row xs={1} md={2} lg={3}>
                                                <Col className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="asana-downtime"
                                                        label="Výpadky webů"
                                                        checked={integrations.asana.settings?.downtime ?? true}
                                                        onChange={() => {}}
                                                    />
                                                </Col>
                                                <Col className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="asana-security"
                                                        label="Bezpečnostní problémy"
                                                        checked={integrations.asana.settings?.security ?? true}
                                                        onChange={() => {}}
                                                    />
                                                </Col>
                                                <Col className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="asana-updates"
                                                        label="Aktualizace WordPress"
                                                        checked={integrations.asana.settings?.updates ?? false}
                                                        onChange={() => {}}
                                                    />
                                                </Col>
                                                <Col className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="asana-plugin-updates"
                                                        label="Aktualizace pluginů"
                                                        checked={integrations.asana.settings?.pluginUpdates ?? false}
                                                        onChange={() => {}}
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => handleTestIntegration('asana')}
                                            disabled={testIntegrationMutation.isLoading}
                                        >
                                            {testIntegrationMutation.isLoading && testIntegrationMutation.variables === 'asana' ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Testování...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-check-circle me-2"></i> Otestovat integraci
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    {/* Trello integrace */}
                    <Tab.Pane eventKey="trello">
                        <Card className="shadow-sm">
                            <Card.Header className="bg-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <div className="me-3">
                                            <i className="fab fa-trello fa-2x text-primary"></i>
                                        </div>
                                        <div>
                                            <h4 className="mb-0">Trello integrace</h4>
                                            <p className="mb-0 text-muted">Vytvářejte karty z monitorovacích upozornění</p>
                                        </div>
                                    </div>
                                    <div>
                                        {integrations?.trello ? (
                                            <div className="d-flex align-items-center">
                                                <Badge bg="success" className="me-3 px-3 py-2">Připojeno</Badge>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDisconnect('trello')}
                                                    disabled={disconnectMutation.isLoading}
                                                >
                                                    {disconnectMutation.isLoading ? (
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                    ) : 'Odpojit'}
                                                </Button>
                                            </div>
                                        ) : (
                                            <Badge bg="secondary" className="px-3 py-2">Nepřipojeno</Badge>
                                        )}
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {!integrations?.trello ? (
                                    <Form onSubmit={handleTrelloSubmit}>
                                        <Row>
                                            <Col lg={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>API klíč</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={trelloForm.apiKey}
                                                        onChange={(e) => setTrelloForm({ ...trelloForm, apiKey: e.target.value })}
                                                        placeholder="a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6"
                                                        required
                                                    />
                                                    <Form.Text className="text-muted">
                                                        API klíč získáte na <a href="https://trello.com/app-key" target="_blank" rel="noopener noreferrer">této stránce</a>.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Token</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={trelloForm.token}
                                                        onChange={(e) => setTrelloForm({ ...trelloForm, token: e.target.value })}
                                                        placeholder="a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6..."
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>ID nástěnky</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={trelloForm.boardId}
                                                        onChange={(e) => setTrelloForm({ ...trelloForm, boardId: e.target.value })}
                                                        placeholder="a1b2c3d4e5f6"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col lg={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>ID seznamu</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={trelloForm.listId}
                                                        onChange={(e) => setTrelloForm({ ...trelloForm, listId: e.target.value })}
                                                        placeholder="a1b2c3d4e5f6"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={connectMutation.isLoading}
                                        >
                                            {connectMutation.isLoading ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Připojování...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fab fa-trello me-2"></i> Připojit Trello
                                                </>
                                            )}
                                        </Button>
                                    </Form>
                                ) : (
                                    <div className="bg-light p-4 rounded">
                                        <div className="mb-4">
                                            <h5>Aktuální nastavení</h5>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <p className="mb-1"><strong>Nástěnka:</strong> {integrations.trello.boardName}</p>
                                                    <p className="mb-0"><strong>Seznam:</strong> {integrations.trello.listName}</p>
                                                </div>
                                                <div className="col-md-6">
                                                    <p className="mb-0"><strong>Poslední synchronizace:</strong> {integrations.trello.lastSync ? new Date(integrations.trello.lastSync).toLocaleString() : 'Žádná synchronizace'}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => handleTestIntegration('trello')}
                                            disabled={testIntegrationMutation.isLoading}
                                        >
                                            {testIntegrationMutation.isLoading && testIntegrationMutation.variables === 'trello' ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Testování...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-check-circle me-2"></i> Otestovat integraci
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    {/* Webhooks integrace */}
                    <Tab.Pane eventKey="webhooks">
                        <Card className="shadow-sm">
                            <Card.Header className="bg-white">
                                <div className="d-flex align-items-center">
                                    <div className="me-3">
                                        <i className="fas fa-plug fa-2x text-primary"></i>
                                    </div>
                                    <div>
                                        <h4 className="mb-0">Webhooks API</h4>
                                        <p className="mb-0 text-muted">Propojte monitoring s vlastními aplikacemi nebo Zapier/Make</p>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-4">
                                    <p>
                                        Webhook API umožňuje propojit WP Monitor s libovolnými externími službami.
                                        Každý webhook obsahuje všechny relevantní informace o události a můžete ho integrovat
                                        s nástroji jako Zapier, Make (dříve Integromat) nebo vlastními aplikacemi.
                                    </p>
                                </div>

                                <div className="api-key mb-4">
                                    <h5 className="mb-3">Váš API klíč</h5>
                                    <div className="d-flex align-items-center">
                                        <code className="p-2 bg-light flex-grow-1 rounded">xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx</code>
                                        <Button variant="outline-primary" className="ms-3">
                                            <i className="fas fa-sync-alt me-2"></i> Vygenerovat nový klíč
                                        </Button>
                                    </div>
                                </div>

                                <div className="webhook-urls mb-4">
                                    <h5 className="mb-3">Webhook URL</h5>
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                            <tr>
                                                <th>Událost</th>
                                                <th>URL</th>
                                                <th>Akce</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td>Výpadek webu</td>
                                                <td>
                                                    <code>https://api.wp-monitor.com/webhooks/downtime</code>
                                                </td>
                                                <td>
                                                    <Button variant="light" size="sm">
                                                        <i className="fas fa-copy me-1"></i> Kopírovat
                                                    </Button>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Obnovení webu</td>
                                                <td>
                                                    <code>https://api.wp-monitor.com/webhooks/uptime</code>
                                                </td>
                                                <td>
                                                    <Button variant="light" size="sm">
                                                        <i className="fas fa-copy me-1"></i> Kopírovat
                                                    </Button>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Bezpečnostní problém</td>
                                                <td>
                                                    <code>https://api.wp-monitor.com/webhooks/security</code>
                                                </td>
                                                <td>
                                                    <Button variant="light" size="sm">
                                                        <i className="fas fa-copy me-1"></i> Kopírovat
                                                    </Button>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="webhook-example">
                                    <h5 className="mb-3">Příklad dat</h5>
                                    <pre className="p-3 bg-light rounded">
{`{
  "event": "downtime",
  "website": {
    "id": 123,
    "url": "https://example.com",
    "name": "Example Website"
  },
  "timestamp": "2023-06-15T14:30:00Z",
  "details": {
    "status": "offline",
    "statusCode": 503,
    "message": "Service Unavailable"
  }
}`}
                                    </pre>
                                </div>
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </Container>
    );
};

export default Integrations;